import React, { useState, useEffect } from 'react';
import { Clock, CircleCheck as CheckCircle, Package, Truck, User, MapPin, CreditCard, Calendar, RefreshCw, TriangleAlert as AlertTriangle, ChefHat, Circle as XCircle, ThumbsUp, Chrome as Home } from 'lucide-react';
import { trackFood, trackError } from '../Utils/analytics';
import { KITCHEN_ORDERS_URL, ORDER_STATUS_UPDATE_URL, DELIVERY_UPDATE_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import './MyKitchenOrdersTab.css';

interface OrderItem {
  food_id: number;
  food_name: string;
  food_image?: string;
  quantity: number;
  price: number;
  subtotal: number;
}

interface OrderCustomer {
  user_id: number;
  name: string;
  phone_number: string;
  email: string;
}

interface Order {
  order_id: number;
  customer: OrderCustomer;
  kitchen_id: number;
  total_quantity: number;
  subtotal: number;
  delivery_fee: number;
  tax_amount: number;
  grand_total: number;
  payment_method: string;
  payment_status: string;
  order_status: string;
  delivery_type: string;
  delivery_address: string;
  delivery_partner_id: number | null;
  created_at: string;
  items?: OrderItem[];
}

interface MyKitchenOrdersTabProps {
  kitchenId: number | null;
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const statusOptions = [
  { value: 'waiting_kitchen_confirmation', label: 'Awaiting Confirmation', color: '#f59e0b', icon: Clock },
  { value: 'confirmed', label: 'Confirmed', color: '#17a2b8', icon: CheckCircle },
  { value: 'accepted', label: 'Accepted', color: '#20c997', icon: ThumbsUp },
  { value: 'preparing', label: 'Preparing', color: '#ffc107', icon: ChefHat },
  { value: 'ready', label: 'Ready', color: '#fd7e14', icon: Package },
  { value: 'picked_up', label: 'Picked Up', color: '#6f42c1', icon: Truck },
  { value: 'delivered', label: 'Delivered', color: '#28a745', icon: Home },
  { value: 'completed', label: 'Completed', color: '#28a745', icon: CheckCircle },
  { value: 'cancelled', label: 'Cancelled', color: '#6c757d', icon: XCircle },
  { value: 'rejected', label: 'Rejected', color: '#dc3545', icon: XCircle },
];

const STATUS_MESSAGES: Record<string, string> = {
  accepted: 'Kitchen accepted the order',
  preparing: 'Food is being prepared',
  ready: 'Food is ready for pickup/delivery',
  picked_up: 'Order picked up by delivery partner',
  delivered: 'Order delivered successfully',
  completed: 'Order completed',
  cancelled: 'Order cancelled',
  rejected: 'Kitchen rejected the order',
};

const SELF_PICKUP_FLOW = ['confirmed', 'accepted', 'preparing', 'ready', 'completed'];
const DELIVERY_PARTNER_FLOW = ['waiting_kitchen_confirmation', 'confirmed', 'accepted', 'preparing', 'ready', 'picked_up', 'delivered'];
const TERMINAL_STATUSES = ['completed', 'delivered', 'cancelled', 'rejected'];

const getStatusMeta = (status: string) => {
  return statusOptions.find(o => o.value === status.toLowerCase());
};

const getStatusColor = (status: string): string => {
  return getStatusMeta(status)?.color || '#6c757d';
};

const getStatusIcon = (status: string) => {
  const meta = getStatusMeta(status);
  const Icon = meta?.icon || Clock;
  return <Icon size={16} />;
};

const getNextStatus = (currentStatus: string, deliveryType: string): string | null => {
  const flow = (deliveryType === 'delivery_partner' || deliveryType === 'kitchen_delivery') ? DELIVERY_PARTNER_FLOW : SELF_PICKUP_FLOW;
  const idx = flow.indexOf(currentStatus.toLowerCase());
  if (idx >= 0 && idx < flow.length - 1) return flow[idx + 1];
  return null;
};

const MyKitchenOrdersTab: React.FC<MyKitchenOrdersTabProps> = ({ kitchenId, showNotification }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Delivery ETA update state
  const [etaEditingOrderId, setEtaEditingOrderId] = useState<number | null>(null);
  const [etaValue, setEtaValue] = useState<string>('');
  const [etaMessage, setEtaMessage] = useState<string>('');
  const [updatingEtaOrderId, setUpdatingEtaOrderId] = useState<number | null>(null);
  const [orderEtas, setOrderEtas] = useState<Record<number, string>>({});

  // Pagination state
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);
  const ORDERS_PAGE_SIZE = 10;

  const fetchKitchenOrders = async (page: number = 1, append: boolean = false) => {
    if (!kitchenId) {
      setError('Kitchen ID not found. Please create a kitchen first.');
      setIsLoading(false);
      return;
    }

    if (!append) setIsLoading(true);
    else setLoadingMoreOrders(true);
    setError(null);

    try {
      const url = `${KITCHEN_ORDERS_URL}?page=${page}&limit=${ORDERS_PAGE_SIZE}`;
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          setError('You do not have permission to view orders.');
        } else if (response.status === 404) {
          setError('No orders found.');
        } else {
          setError('Failed to fetch orders. Please try again later.');
        }
        setIsLoading(false);
        return;
      }

      const json = await response.json();
      const allOrders: Order[] = json.orders || [];
      const total: number = json.total_orders ?? allOrders.length;
      setOrdersTotal(total);
      setOrders(prev => append ? [...prev, ...allOrders] : allOrders);

      if (!append && allOrders.length === 0) {
        setError('No orders found for your kitchen yet.');
      }

    } catch {
      setError('Network error. Please check your connection and try again.');
      trackError('kitchen_orders_fetch_failed', 'kitchen_orders_tab');
    } finally {
      setIsLoading(false);
      setLoadingMoreOrders(false);
    }
  };

  const handleLoadMoreOrders = () => {
    const nextPage = ordersPage + 1;
    setOrdersPage(nextPage);
    fetchKitchenOrders(nextPage, true);
  };

  const handleUpdateEta = async (orderId: number) => {
    if (!etaValue.trim()) {
      showNotification('error', 'ETA Required', 'Please enter an estimated delivery time.');
      return;
    }
    setUpdatingEtaOrderId(orderId);
    try {
      const body: Record<string, string> = { new_eta: etaValue.trim() };
      if (etaMessage.trim()) body.customer_message = etaMessage.trim();
      const res = await authenticatedFetch(DELIVERY_UPDATE_URL(orderId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const confirmedEta = data.new_eta || data.eta || etaValue.trim();
        setOrderEtas(prev => ({ ...prev, [orderId]: confirmedEta }));
        setEtaEditingOrderId(null);
        setEtaValue('');
        setEtaMessage('');
        showNotification('success', 'ETA Updated', `Customer notified. New ETA: ${confirmedEta}`);
      } else {
        const errData = await res.json().catch(() => ({}));
        showNotification('error', 'ETA Update Failed', errData.message || 'Could not update ETA. Please try again.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
    } finally {
      setUpdatingEtaOrderId(null);
    }
  };

  const openEtaEditor = (orderId: number) => {
    setEtaEditingOrderId(orderId);
    setEtaValue(orderEtas[orderId] || '');
    setEtaMessage('');
  };

  const cancelEtaEditor = () => {
    setEtaEditingOrderId(null);
    setEtaValue('');
    setEtaMessage('');
  };

  const handleUpdateStatus = async (orderId: number, newStatus: string) => {
    setUpdatingOrderId(orderId);

    try {
      const response = await authenticatedFetch(`${ORDER_STATUS_UPDATE_URL(orderId)}?status=${newStatus}`, {
        method: 'POST',
        headers: { 'accept': 'application/json' },
      });

      if (response.ok) {
        setOrders(prev =>
          prev.map(order =>
            order.order_id === orderId
              ? { ...order, order_status: newStatus }
              : order
          )
        );

        const message = STATUS_MESSAGES[newStatus] || `Order #${orderId} marked as ${newStatus}`;
        showNotification('success', 'Status Updated', message);
        trackFood('food_updated', `order_${orderId}_status_${newStatus}`);

        setTimeout(() => { fetchKitchenOrders(); }, 1000);
      } else {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 404) {
          showNotification('error', 'Order Not Found', 'The order could not be found or has been deleted.');
        } else if (response.status === 401) {
          showNotification('error', 'Authentication Failed', 'Please log in again.');
        } else if (response.status === 422) {
          showNotification('error', 'Invalid Status', 'The status value is not valid for this order.');
        } else {
          showNotification('error', 'Update Failed', errorData.message || 'Failed to update order status.');
        }

        trackError('order_status_update_failed', 'kitchen_orders_tab');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
      trackError('order_status_update_network_error', 'kitchen_orders_tab');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    fetchKitchenOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitchenId]);

  if (!kitchenId) {
    return (
      <div className="user-profile-empty-state">
        <ChefHat size={60} />
        <p>Please create a kitchen first to view orders.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="user-profile-empty-state">
        <div className="user-profile-loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }}></div>
        <p>Loading your kitchen orders...</p>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="user-profile-empty-state">
        <AlertTriangle size={60} />
        <p>{error}</p>
        <button onClick={() => { setOrdersPage(1); fetchKitchenOrders(1); }} className="user-profile-edit-btn" style={{ marginTop: '1rem' }}>
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="kitchen-orders-container">
      <div className="kitchen-orders-header">
        <div className="orders-title-section">
          <h4>Kitchen Orders</h4>
          <p>Manage orders received for your kitchen</p>
        </div>
        <button onClick={() => { setOrdersPage(1); fetchKitchenOrders(1); }} className="user-profile-edit-btn" disabled={isLoading}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {orders.length > 0 && (
        <div className="orders-filter-bar">
          {['all', 'waiting_kitchen_confirmation', 'confirmed', 'accepted', 'preparing', 'ready', 'delivered', 'completed', 'rejected', 'cancelled'].map(s => {
            const count = s === 'all' ? orders.length : orders.filter(o => o.order_status.toLowerCase() === s).length;
            if (s !== 'all' && count === 0) return null;
            const label = s === 'waiting_kitchen_confirmation' ? 'Awaiting Confirmation' : s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ');
            return (
              <button
                key={s}
                className={`orders-filter-btn${statusFilter === s ? ' active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {label}
                <span className="orders-filter-count">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="user-profile-empty-state">
          <Package size={60} />
          <p>No orders received yet. Start promoting your kitchen!</p>
        </div>
      ) : (
        <>
        <div className="orders-list">
          {orders.filter(o => statusFilter === 'all' || o.order_status.toLowerCase() === statusFilter).map(order => {
            const currentStatus = order.order_status.toLowerCase();
            const nextStatus = getNextStatus(currentStatus, order.delivery_type);
            const isTerminal = TERMINAL_STATUSES.includes(currentStatus);
            const isUpdating = updatingOrderId === order.order_id;
            const isConfirmed = currentStatus === 'confirmed';

            return (
              <div key={order.order_id} className="order-card">
                {/* Order Header */}
                <div className="order-header">
                  <div className="order-id-section">
                    <h5>Order #{order.order_id}</h5>
                    <div
                      className="order-status-badge"
                      style={{ backgroundColor: getStatusColor(currentStatus), color: 'white' }}
                    >
                      {getStatusIcon(currentStatus)}
                      <span>{currentStatus.replace('_', ' ').charAt(0).toUpperCase() + currentStatus.replace('_', ' ').slice(1)}</span>
                    </div>
                  </div>
                  <div className="order-date">
                    <Calendar size={14} />
                    <span>{formatDate(order.created_at)}</span>
                  </div>
                </div>

                {/* Order Details */}
                <div className="order-details-grid">
                  <div className="order-detail">
                    <span className="detail-label">Quantity</span>
                    <span className="detail-value">{order.total_quantity}</span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Subtotal</span>
                    <span className="detail-value">£{order.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Delivery Fee</span>
                    <span className="detail-value">£{order.delivery_fee.toFixed(2)}</span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Tax</span>
                    <span className="detail-value">£{order.tax_amount.toFixed(2)}</span>
                  </div>

                {/* Item breakdown */}
                {order.items && order.items.length > 0 && (
                  <div className="order-detail order-detail--full">
                    <span className="detail-label">Items</span>
                    <div className="order-items-breakdown">
                      {order.items.map(item => (
                        <div key={item.food_id} className="order-item-row">
                          {item.food_image && (
                            <img
                              src={item.food_image}
                              alt={item.food_name}
                              className="order-item-img"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          )}
                          <span className="order-item-name">{item.food_name}</span>
                          <span className="order-item-qty">×{item.quantity}</span>
                          <span className="order-item-price">£{item.subtotal.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                  <div className="order-detail">
                    <span className="detail-label">Total</span>
                    <span className="detail-value price">£{order.grand_total.toFixed(2)}</span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Payment</span>
                    <span className="detail-value">
                      <CreditCard size={13} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      {order.payment_method}
                      {' '}
                      <span style={{
                        fontSize: '0.75rem',
                        color: order.payment_status === 'paid' ? '#28a745' : '#6c757d',
                        fontWeight: 500,
                      }}>
                        ({order.payment_status})
                      </span>
                    </span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Customer</span>
                    <span className="detail-value">{order.customer.name}</span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Phone</span>
                    <span className="detail-value">{order.customer.phone_number}</span>
                  </div>
                </div>

                {/* Delivery Information */}
                <div className="delivery-info1-section">
                  {(order.delivery_type === 'delivery_partner' || order.delivery_type === 'kitchen_delivery') ? (
                    <div className="delivery-info1">
                      <div className="delivery-type">
                        <Truck size={16} />
                        <span>Kitchen Delivery</span>
                      </div>
                      {order.delivery_address && (
                        <div className="delivery-address">
                          <MapPin size={14} />
                          <span>{order.delivery_address}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="delivery-info1">
                      <div className="delivery-type">
                        <User size={16} />
                        <span>Self Pickup</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery ETA update for kitchen_delivery orders in active states */}
                {order.delivery_type === 'kitchen_delivery' && ['accepted', 'preparing', 'ready'].includes(currentStatus) && (
                  <div className="eta-update-section">
                    {orderEtas[order.order_id] && etaEditingOrderId !== order.order_id && (
                      <div className="eta-current">
                        <Clock size={14} />
                        <span>Current ETA: <strong>{orderEtas[order.order_id]}</strong></span>
                      </div>
                    )}
                    {etaEditingOrderId === order.order_id ? (
                      <div className="eta-editor">
                        <div className="eta-editor-row">
                          <input
                            type="text"
                            className="eta-input"
                            placeholder="e.g. 30 mins"
                            value={etaValue}
                            onChange={e => setEtaValue(e.target.value)}
                            disabled={updatingEtaOrderId === order.order_id}
                          />
                          <input
                            type="text"
                            className="eta-message-input"
                            placeholder="Message to customer (optional)"
                            value={etaMessage}
                            onChange={e => setEtaMessage(e.target.value)}
                            disabled={updatingEtaOrderId === order.order_id}
                          />
                        </div>
                        <div className="eta-editor-actions">
                          <button
                            className="eta-btn eta-btn--cancel"
                            onClick={cancelEtaEditor}
                            disabled={updatingEtaOrderId === order.order_id}
                          >
                            Cancel
                          </button>
                          <button
                            className="eta-btn eta-btn--submit"
                            onClick={() => handleUpdateEta(order.order_id)}
                            disabled={updatingEtaOrderId === order.order_id}
                          >
                            {updatingEtaOrderId === order.order_id ? (
                              <><div className="user-profile-loading-spinner" style={{ width: '14px', height: '14px' }} /><span>Updating...</span></>
                            ) : (
                              <><Clock size={14} /><span>Update ETA</span></>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        className="eta-btn eta-btn--edit"
                        onClick={() => openEtaEditor(order.order_id)}
                      >
                        <Clock size={14} />
                        {orderEtas[order.order_id] ? 'Update ETA' : 'Set ETA'}
                      </button>
                    )}
                  </div>
                )}

                {/* Status Update Actions */}
                {!isTerminal && currentStatus !== 'waiting_kitchen_confirmation' && (
                  <div className="order-actions">
                    {isConfirmed && (
                      <button
                        onClick={() => handleUpdateStatus(order.order_id, 'rejected')}
                        disabled={isUpdating}
                        className="status-update-btn"
                        style={{ backgroundColor: '#dc3545', color: 'white' }}
                      >
                        {isUpdating ? (
                          <><div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }}></div><span>Updating...</span></>
                        ) : (
                          <><XCircle size={16} /><span>Reject</span></>
                        )}
                      </button>
                    )}
                    {nextStatus && (
                      <button
                        onClick={() => handleUpdateStatus(order.order_id, nextStatus)}
                        disabled={isUpdating}
                        className="status-update-btn"
                        style={{ backgroundColor: getStatusColor(nextStatus), color: 'white' }}
                      >
                        {isUpdating ? (
                          <><div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }}></div><span>Updating...</span></>
                        ) : (
                          <>{getStatusIcon(nextStatus)}<span>Mark as {getStatusMeta(nextStatus)?.label || nextStatus}</span></>
                        )}
                      </button>
                    )}
                  </div>
                )}

                {currentStatus === 'waiting_kitchen_confirmation' && (
                  <div className="order-waiting-notice" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.7rem 1rem', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '10px', fontSize: '0.84rem', color: '#92400e', marginTop: '0.75rem' }}>
                    <Clock size={15} style={{ flexShrink: 0, color: '#f59e0b' }} />
                    <span>Respond to this delivery request in the <strong>Delivery Requests</strong> tab.</span>
                  </div>
                )}

                {currentStatus === 'completed' && (
                  <div className="order-completed-notice">
                    <CheckCircle size={16} />
                    <span>{STATUS_MESSAGES.completed}</span>
                  </div>
                )}

                {currentStatus === 'delivered' && (
                  <div className="order-completed-notice">
                    <Home size={16} />
                    <span>{STATUS_MESSAGES.delivered}</span>
                  </div>
                )}

                {currentStatus === 'rejected' && (
                  <div className="order-completed-notice" style={{ color: '#dc3545', borderColor: '#dc3545' }}>
                    <XCircle size={16} />
                    <span>{STATUS_MESSAGES.rejected}</span>
                  </div>
                )}

                {currentStatus === 'cancelled' && (
                  <div className="order-completed-notice" style={{ color: '#6c757d', borderColor: '#6c757d' }}>
                    <XCircle size={16} />
                    <span>{STATUS_MESSAGES.cancelled}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Load More button for kitchen orders */}
        {orders.length > 0 && orders.length < ordersTotal && (
          <div className="orders-load-more">
            <button
              className="user-profile-edit-btn"
              onClick={handleLoadMoreOrders}
              disabled={loadingMoreOrders}
            >
              {loadingMoreOrders ? (
                <><div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }} /><span>Loading...</span></>
              ) : (
                <><RefreshCw size={16} /><span>Load More</span></>
              )}
            </button>
            <span className="orders-count">{orders.length} of {ordersTotal} orders</span>
          </div>
        )}
        </>
      )}

      {error && orders.length > 0 && (
        <div className="kitchen-orders-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default MyKitchenOrdersTab;
